import { useMemo, useState } from "react";

type Node = { id: string; name: string; count: number; pos?: number; neg?: number };
type Edge = { x: string; y: string; w: number };
const nfmt = (x: number) => (x >= 100000 ? Intl.NumberFormat("id-ID", { notation: "compact", maximumFractionDigits: 1 }).format(x) : Intl.NumberFormat("id-ID").format(x));
const TYPE_COLOR: Record<string, string> = { topic: "#1F3864", person: "#8a4f9e", organization: "#2a7f8f", brand: "#2a7f8f", emotion: "#e2711d", attribute: "#6b7a99", location: "#2e9e5b" };

/** Warna node = sentimen dominan (merah→abu→hijau) */
function toneColor(n: Node) {
  const t = n.count || 1, negS = (n.neg ?? 0) / t, posS = (n.pos ?? 0) / t;
  if (negS >= 0.35) return "var(--mark-neg)";
  if (posS >= 0.35) return "var(--mark-pos)";
  return "var(--mark-neu)";
}

/** Simulasi gaya deterministik kecil: repulsi + pegas + gravitasi pusat. */
function layout(nodes: Node[], edges: Edge[], W: number, H: number) {
  const N = nodes.length;
  const idx = new Map(nodes.map((n, i) => [n.id, i]));
  const px = new Array(N).fill(0), py = new Array(N).fill(0);
  for (let i = 0; i < N; i++) { // mulai melingkar, terbesar (index 0) di pusat
    const a = (i / Math.max(1, N - 1)) * Math.PI * 2;
    const r = i === 0 ? 0 : 90 + (i % 5) * 28;
    px[i] = W / 2 + Math.cos(a * 2.399) * r; py[i] = H / 2 + Math.sin(a * 2.399) * r;
  }
  const E = edges.map((e) => ({ a: idx.get(e.x)!, b: idx.get(e.y)!, w: e.w })).filter((e) => e.a != null && e.b != null);
  const maxW = Math.max(1, ...E.map((e) => e.w));
  for (let it = 0; it < 160; it++) {
    const fx = new Array(N).fill(0), fy = new Array(N).fill(0);
    for (let i = 0; i < N; i++) for (let j = i + 1; j < N; j++) {
      let dx = px[i] - px[j], dy = py[i] - py[j];
      const d2 = Math.max(120, dx * dx + dy * dy), f = 11000 / d2, d = Math.sqrt(d2);
      dx /= d; dy /= d;
      fx[i] += dx * f; fy[i] += dy * f; fx[j] -= dx * f; fy[j] -= dy * f;
    }
    for (const e of E) { // pegas: bobot besar = jarak ideal pendek (relevansi = kedekatan)
      const ideal = 170 - 110 * (e.w / maxW);
      let dx = px[e.b] - px[e.a], dy = py[e.b] - py[e.a];
      const d = Math.max(10, Math.hypot(dx, dy)), f = (d - ideal) * 0.045;
      dx /= d; dy /= d;
      fx[e.a] += dx * f; fy[e.a] += dy * f; fx[e.b] -= dx * f; fy[e.b] -= dy * f;
    }
    for (let i = 0; i < N; i++) {
      fx[i] += (W / 2 - px[i]) * 0.012; fy[i] += (H / 2 - py[i]) * 0.012;
      px[i] = Math.min(W - 46, Math.max(46, px[i] + fx[i]));
      py[i] = Math.min(H - 44, Math.max(38, py[i] + fy[i]));
    }
  }
  return { px, py, idx };
}

export function NetworkGraph({ nodes, edges, height = 420, onNode, hrefOf }:{
  nodes: Node[]; edges: Edge[]; height?: number;
  onNode?: (n: Node) => void; hrefOf?: (n: Node) => string;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const W = 860, H = height;
  const { px, py, idx } = useMemo(() => layout(nodes, edges, W, H), [nodes, edges, H]);
  if (nodes.length === 0) return <div className="avio-empty"><i className="fa-solid fa-circle-nodes avio-empty__icon" /><div className="avio-empty__title">Belum ada data label pada rentang ini.</div></div>;
  const maxC = Math.max(1, ...nodes.map((n) => n.count));
  const maxW = Math.max(1, ...edges.map((e) => e.w));
  const R = (n: Node) => 10 + Math.sqrt(n.count / maxC) * 26;
  const neigh = new Set<string>();
  if (hover) { neigh.add(hover); for (const e of edges) { if (e.x === hover) neigh.add(e.y); if (e.y === hover) neigh.add(e.x); } }
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", background: "var(--color-surface-sunken)", borderRadius: "var(--radius-md)", border: "1px solid var(--color-border)" }}>
      {edges.map((e, i) => {
        const a = idx.get(e.x), b = idx.get(e.y);
        if (a == null || b == null) return null;
        const dim = hover && !(e.x === hover || e.y === hover);
        return <line key={i} x1={px[a]} y1={py[a]} x2={px[b]} y2={py[b]}
                     stroke={dim ? "var(--mark-grid)" : "#C3CCDB"} strokeWidth={0.8 + (e.w / maxW) * 4.5} strokeLinecap="round">
          <title>{`${nodes[a].name} ↔ ${nodes[b].name}: ${e.w}× bersama`}</title>
        </line>;
      })}
      {nodes.map((n) => {
        const i = idx.get(n.id)!;
        const dim = hover && !neigh.has(n.id);
        const el = (
          <g key={n.id} transform={`translate(${px[i]},${py[i]})`} opacity={dim ? 0.25 : 1}
             onMouseEnter={() => setHover(n.id)} onMouseLeave={() => setHover(null)}
             onClick={() => onNode?.(n)} style={{ cursor: "pointer" }}>
            <circle r={R(n)} fill={toneColor(n)} fillOpacity={0.88} stroke="#fff" strokeWidth={2} />
            <title>{`${n.name}: ${n.count} mention (${n.pos ?? 0}+ / ${n.neg ?? 0}-) — klik utk pendalaman`}</title>
            {(n.count / maxC > 0.30 || hover === n.id) && (
              <text y={R(n) + 17} textAnchor="middle" fontSize={11} fontWeight={600}
                fill="var(--color-text)" stroke="var(--color-surface-sunken)" strokeWidth={3.5}
                paintOrder="stroke" style={{ pointerEvents: "none" }}>
                {n.name.length > 18 ? n.name.slice(0, 17) + "…" : n.name}
              </text>
            )}
            <text y={4} textAnchor="middle" fontSize={10.5} fontWeight={700} fill="#fff" style={{ pointerEvents: "none" }}>{nfmt(n.count)}</text>
          </g>
        );
        return hrefOf && !onNode ? <a key={n.id} href={hrefOf(n)}>{el}</a> : el;
      })}
    </svg>
  );
}

/** Tampilan pendalaman: pusat + satelit radial berwarna per tipe label. */
export function FocusView({ data, onBack, postHref, satHref }:{
  data: { center: { id: string; name: string; labelType: string; tones: Record<string, number> }; satellites: Array<{ id: string; name: string; labelType: string; count: number }>; topPosts: any[] };
  onBack: () => void; postHref: string; satHref?: (id: string) => string;
}) {
  const W = 560, H = 400, cx = W / 2, cy = H / 2;
  const t = data.center.tones, tot = (t.positive ?? 0) + (t.neutral ?? 0) + (t.negative ?? 0);
  const maxC = Math.max(1, ...data.satellites.map((s) => s.count));
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 14 }}>
      <div>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", background: "#fbfcff", borderRadius: 10, border: "1px solid #e3e9f4" }}>
          {data.satellites.map((s2, i) => {
            const a = (i / Math.max(1, data.satellites.length)) * Math.PI * 2 - Math.PI / 2;
            const r = 120 + (i % 2) * 42;
            const x = cx + Math.cos(a) * r, y = cy + Math.sin(a) * r;
            const rad = 8 + Math.sqrt(s2.count / maxC) * 14;
            return (
              <g key={s2.id + i}>
                <line x1={cx} y1={cy} x2={x} y2={y} stroke="#c9d4e8" strokeWidth={0.8 + (s2.count / maxC) * 3} />
                <a href={satHref ? satHref(s2.id) : undefined}>
                  <g transform={`translate(${x},${y})`} style={{ cursor: "pointer" }}>
                    <circle r={rad} fill={TYPE_COLOR[s2.labelType] ?? "#6b7a99"} fillOpacity={0.9} stroke="#fff" strokeWidth={1.5} />
                    <title>{`${s2.name} (${s2.labelType}): ${s2.count}× bersama`}</title>
                    <text y={rad + 10} textAnchor="middle" fontSize={9.5} fill="#1c2333">{s2.name.length > 16 ? s2.name.slice(0, 15) + "…" : s2.name}</text>
                  </g>
                </a>
              </g>
            );
          })}
          <g transform={`translate(${cx},${cy})`}>
            <circle r={40} fill="#1F3864" stroke="#fff" strokeWidth={3} />
            <text textAnchor="middle" y={-2} fontSize={11} fontWeight={700} fill="#fff">{data.center.name.length > 14 ? data.center.name.slice(0, 13) + "…" : data.center.name}</text>
            <text textAnchor="middle" y={13} fontSize={10} fill="#cfe0ff">{tot} mention</text>
          </g>
        </svg>
        <p style={{ fontSize: 11, color: "#888", margin: "6px 0 0" }}>
          Satelit = label yang paling sering muncul bersama · warna: <span style={{ color: TYPE_COLOR.topic }}>■ topic</span> <span style={{ color: TYPE_COLOR.person }}>■ person</span> <span style={{ color: TYPE_COLOR.emotion }}>■ emotion</span> <span style={{ color: TYPE_COLOR.location }}>■ location</span> <span style={{ color: TYPE_COLOR.brand }}>■ brand/org</span> <span style={{ color: TYPE_COLOR.attribute }}>■ attribute</span>
        </p>
      </div>
      <div style={{ fontSize: 13 }}>
        <button onClick={onBack} style={{ padding: "5px 12px", marginBottom: 8, cursor: "pointer", border: "1px solid #dfe6f3", borderRadius: 7, background: "#fff" }}>
          <i className="fa-solid fa-arrow-left" /> Kembali ke graf
        </button>
        <h4 style={{ margin: "4px 0 6px" }}>{data.center.name}</h4>
        {tot > 0 && (
          <div style={{ display: "flex", height: 14, borderRadius: 4, overflow: "hidden", marginBottom: 8 }}>
            {/* SVG <title> pada node & edge sudah menjadi tooltip; bar mini ini
                sebelumnya hanya memakai atribut title bawaan yang tidak pernah
                muncul di peranti sentuh. Angka ditulis eksplisit di bawahnya
                supaya tetap terbaca tanpa hover sama sekali. */}
            <div title={`positif ${t.positive}`} style={{ width: `${((t.positive ?? 0) / tot) * 100}%`, background: "#2e9e5b" }} />
            <div title={`netral ${t.neutral}`} style={{ width: `${((t.neutral ?? 0) / tot) * 100}%`, background: "#9aa5b5" }} />
            <div title={`negatif ${t.negative}`} style={{ width: `${((t.negative ?? 0) / tot) * 100}%`, background: "#d64545" }} />
          </div>
        )}
        <a href={postHref} style={{ fontSize: 12.5 }}><i className="fa-solid fa-list" /> Lihat semua item label ini</a>
        <h5 style={{ margin: "12px 0 4px" }}>Item teratas</h5>
        {data.topPosts.map((p) => (
          <p key={p.id} style={{ margin: "0 0 6px", fontSize: 12, lineHeight: 1.4 }}>
            <span style={{ color: p.sentimentLabel === "negative" ? "#d64545" : p.sentimentLabel === "positive" ? "#2e9e5b" : "#888" }}>●</span>{" "}
            {((p.title || p.content || "").replace(/\s+/g, " ")).slice(0, 90)}
          </p>
        ))}
      </div>
    </div>
  );
}
